import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { Router } from '@angular/router';
import { CaseBattlesService } from '../case-battles.service';
import {
  CaseBattlesSlotsVariants,
  CaseBattlesStatusEnum,
  ICaseBattle,
} from '../interfaces/case-battles.interface';
import { ICaseWithQuantity } from '@features/cases/interfaces/cases.interface';
import { SessionService } from '@core/services/session.service';
import { CbListModeComponent } from '../cb-list-mode/cb-list-mode.component';
import { GameIconComponent } from '../../../core/components/game-icon/game-icon.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { ModalService } from '@features/modals/services/modal.service';
import { CbCasesProgressComponent } from '../cb-cases-progress/cb-cases-progress.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { BalanceType } from '@core/types/balance.type';
import { CostComponent } from '../../../core/components/cost/cost.component';
import { UIService } from '@core/services/ui.service';
import { CasesService } from '@features/cases/services/cases.service';

@Component({
  selector: 'app-cb-list-item',
  templateUrl: './cb-list-item.component.html',
  styleUrls: ['./cb-list-item.component.scss'],
  imports: [
    CbListModeComponent,
    GameIconComponent,
    SvgIconComponent,
    CbCasesProgressComponent,
    CostComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInAnimation],
})
export class CbListItemComponent {
  private modalService = inject(ModalService);
  private router = inject(Router);
  public sessionService = inject(SessionService);
  private cbService = inject(CaseBattlesService);
  public uiService = inject(UIService);

  game = input.required<ICaseBattle>();

  casesProgressSettings = computed<{ perView: number }>(() => {
    const breakpoints = this.uiService.breakpoints();
    const settings: Record<
      Exclude<keyof typeof breakpoints, 'max_lg' | 'max_xl2'>,
      { perView: number }
    > = {
      xs: { perView: 2 },
      xsm: { perView: 3 },
      xsl: { perView: 4 },
      sm: { perView: 4 },
      md: { perView: 9 },
      lg: { perView: 11 },
      xl: { perView: 12 },
      xl2: { perView: 5 },
      xl3: { perView: 6 },
      xl4: { perView: 10 },
    };

    const activeBreakpoint = Object.keys(breakpoints).find(
      (key) => breakpoints[key as keyof typeof breakpoints]
    );
    const activeBreakpointSettings =
      settings[activeBreakpoint as keyof typeof settings];

    return activeBreakpointSettings;
  });

  playerBalance = computed<BalanceType>(() => {
    const isParticipant = this.game()?.participants.find(
      (participant) =>
        participant.steamid === this.sessionService.profile()?.steamid
    );
    if (isParticipant) {
      return isParticipant.balance;
    }
    return this.sessionService.balanceType();
  });

  public readonly STATUSES: Record<keyof typeof CaseBattlesStatusEnum, string> =
    {
      open: 'waiting',
      running: 'running',
      finished: 'ended',
    };

  handleOpenSkinsPopup(caseItem: ICaseWithQuantity) {
    this.modalService.openModal('CaseBattlesCaseSkinsModalComponent', {
      caseItem: caseItem,
      balance: this.playerBalance(),
    });
  }

  public handleViewBattle(battle: ICaseBattle) {
    this.router.navigate([`/battles/${battle.uid}`], {
      state: {
        navigationFromList: true,
        skipTie: battle.status === 'finished' && battle.tie,
      },
    });
  }

  public callBotsBattle() {
    this.cbService.openBattles();
    this.cbService.callBots(this.game().uid);
  }

  public handleJoinBattle() {
    this.cbService.joinBattle({
      uid: this.game().uid,
      slot: this.findAvailableSlot(),
    });
  }

  private findAvailableSlot(): CaseBattlesSlotsVariants {
    const playersArray = Array.from(
      { length: this.game().max_participants },
      (_, index) => this.game().participants[index] ?? null
    );
    const firstAvailableSlot =
      playersArray.findIndex((player) => player === null) + 1;

    return firstAvailableSlot as CaseBattlesSlotsVariants;
  }
}
